import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { FileText, ArrowLeft } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';

const Terms = () => {
    const { settings } = useSettings();
    const businessName = settings?.business_name || 'BGRemove';

    return (
        <div className="min-h-screen bg-foreground">
            {/* Header */}
            <div className="container mx-auto px-4 py-6">
                <Link to="/">
                    <Button variant="ghost" className="mb-4">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Home
                    </Button>
                </Link>
            </div>

            {/* Content */}
            <div className="container mx-auto px-4 py-12 sm:py-16">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-12">
                        <div className="w-16 h-16 rounded-2xl bg-primary mx-auto flex items-center justify-center mb-4">
                            <FileText className="w-8 h-8 text-primary-foreground" />
                        </div>
                        <h1 className="font-heading font-bold text-4xl sm:text-5xl text-background tracking-tight mb-4">
                            Terms of Service
                        </h1>
                        <p className="text-background/60">
                            Last updated: January 24, 2026
                        </p>
                    </div>

                    <div className="bg-card rounded-2xl p-8 sm:p-12 shadow-xl space-y-8 text-foreground">
                        <section>
                            <h2 className="text-2xl font-bold mb-4">1. Acceptance of Terms</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                By accessing and using {businessName} ("the Service"), you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to these terms, please do not use the Service.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">2. Use License</h2>
                            <p className="text-muted-foreground leading-relaxed mb-4">
                                Permission is granted to temporarily download one copy of the materials on {businessName} for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                                <li>Modify or copy the materials</li>
                                <li>Use the materials for any commercial purpose without a valid subscription</li>
                                <li>Attempt to decompile or reverse engineer any software contained on the Service</li>
                                <li>Remove any copyright or other proprietary notations from the materials</li>
                                <li>Transfer the materials to another person or "mirror" the materials on any other server</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">3. Credit-Based Pricing Plans</h2>
                            <p className="text-muted-foreground leading-relaxed mb-4">
                                {businessName} offers a flexible credit-based system with three options:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                                <li><strong>Free Plan:</strong> 2 free credits to try our service</li>
                                <li><strong>Subscription Plans:</strong> Monthly recurring credits starting at $4.99/month for 50 credits. Unused credits roll over as long as you remain subscribed. Plans available: 50, 100, 300, 500, and 1000 credits/month</li>
                                <li><strong>Top-Up Credits:</strong> One-time purchase credits valid for 2 years from purchase date. Pricing starts at $2.99 for 10 credits</li>
                            </ul>
                            <p className="text-muted-foreground leading-relaxed mt-4">
                                Subscriptions automatically renew unless cancelled before the renewal date. You may cancel, upgrade, or downgrade your subscription at any time from your account dashboard. We offer a 14-day money-back guarantee for new subscriptions.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">4. User Content</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                You retain all rights to the images you upload to {businessName}. We process your images solely to remove backgrounds and do not claim ownership of your content. We do not store your images permanently on our servers and they are automatically deleted after processing.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">5. Prohibited Uses</h2>
                            <p className="text-muted-foreground leading-relaxed mb-4">
                                You may not use the Service:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                                <li>For any unlawful purpose or to solicit others to perform unlawful acts</li>
                                <li>To violate any international, federal, provincial or state regulations, rules, laws, or local ordinances</li>
                                <li>To infringe upon or violate our intellectual property rights or the intellectual property rights of others</li>
                                <li>To upload or transmit viruses or any other type of malicious code</li>
                                <li>To collect or track the personal information of others</li>
                                <li>To spam, phish, harm, pretext, spider, crawl, or scrape</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">6. Disclaimer</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                The materials on {businessName} are provided on an 'as is' basis. {businessName} makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">7. Limitations</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                In no event shall {businessName} or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on {businessName}, even if {businessName} or an authorized representative has been notified orally or in writing of the possibility of such damage.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">8. Refund Policy</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                We offer a 7-day money-back guarantee for new premium subscriptions. If you're not satisfied with the service, you may request a full refund within 7 days of your initial purchase. Refunds are not available for subscription renewals.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">9. Revisions</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                {businessName} may revise these terms of service at any time without notice. By using this Service you are agreeing to be bound by the then current version of these terms of service.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">10. Contact Information</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                If you have any questions about these Terms of Service, please contact us at{' '}
                                <a href="mailto:info@backgroundremove.uk" className="text-primary hover:underline">
                                    info@backgroundremove.uk
                                </a>
                            </p>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Terms;
